import { useEffect, useRef } from 'react';
import type Uppy from '@uppy/core';
import type { UppyMeta, UppyBody } from '../../../components/machines/universal-media-uploader-types';
import type { MultiFileEntry } from '../../../components/machines/multi-file-uploader-types';
import type { HydratedMediaItem } from '../media-type-config';

interface UseCommittedFileAutoRemovalParams {
  editing: boolean;
  uppy: Uppy<UppyMeta, UppyBody> | null;
  files: Map<string, MultiFileEntry>;
  hydratedItemsByMediaId: Map<string, HydratedMediaItem>;
  suppressFileRemoved: (fileId: string) => void;
  unsuppressFileRemoved: (fileId: string) => void;
}

/**
 * Auto-removes committed files from Uppy's internal queue once their item
 * appears in the field value. Uses suppressFileRemoved so the machine's
 * files entry — and therefore the status card — is preserved while Uppy's
 * slot counters are kept accurate.
 *
 * Tracks removed file IDs per edit session to prevent duplicate removeFile
 * calls on re-renders. The tracking set is cleared when editing ends.
 */
export function useCommittedFileAutoRemoval({
  editing,
  uppy,
  files,
  hydratedItemsByMediaId,
  suppressFileRemoved,
  unsuppressFileRemoved,
}: UseCommittedFileAutoRemovalParams): void {
  const autoRemovedFromUppyRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!editing) {
      autoRemovedFromUppyRef.current.clear();
    }
  }, [editing]);

  useEffect(() => {
    if (!editing || !uppy) return;

    files.forEach((entry, fileId) => {
      if (!entry.attachmentData?.id) return;
      if (!hydratedItemsByMediaId.has(entry.attachmentData.id)) return;
      if (autoRemovedFromUppyRef.current.has(fileId)) return;

      const stillInUppy = uppy.getFile(fileId);
      if (stillInUppy) {
        try {
          suppressFileRemoved(fileId);
          uppy.removeFile(fileId);
          autoRemovedFromUppyRef.current.add(fileId);
        } catch (error) {
          // Roll back the suppress so the next file-removed event for this ID
          // isn't silently swallowed. autoRemovedFromUppyRef is not updated,
          // so a later render pass will retry the removal.
          unsuppressFileRemoved(fileId);
          console.error('Failed to auto-remove committed file from Uppy queue:', error);
        }
      }
    });
  }, [editing, uppy, files, suppressFileRemoved, unsuppressFileRemoved, hydratedItemsByMediaId]);
}
